import type {
  IPushChannelListener,
  TPushChannelCloseListener,
  TPushChannelListener,
  TPushEventListener,
  TPushChannelOpenListener,
} from "./Header.ts";

import {
  atKeys,
  notNullish,
  notEmpty,
  parseArrayAs,
  parseBoolean,
  parseFunction,
  parseRecord,
  parseString,
  parseDefault,
  parseDefaultString,
  parseDefaultNumber,
  isString,
  handleRejection,
} from "@de-xima/xima-common-js-lang";
import { globalEval } from "jquery";

import { redirect } from "./Navigation.ts";
import { showNotification } from "./Notification.ts";
import { stringToSeverity } from "./NotificationHistory.ts";
import { runRemoteCommand } from "./Util.ts";
import { byId } from "@de-xima/xima-common-js-dom";

type TConnectCallback = {
  resolve: () => void;
  reject: (error: Error) => void;
};

let ViewInstance: ViewPushChannelListener | undefined;
let SessionInstance: SessionPushChannelListener | undefined;
let ApplicationInstance: ApplicationPushChannelListener | undefined;

function parseServerCallbackParams(data: unknown): PrimeFaces.ajax.RequestParameter[] {
  const paramsArray = [] as PrimeFaces.ajax.RequestParameter[];
  const params = parseRecord(data);
  for (const key of Object.keys(params)) {
    paramsArray.push({ name: key, value: params[key] });
  }
  return paramsArray;
}

/**
 * Base class for push channel listeners. Implements common message types such
 * as performing an AJAX update or invoking a remote command.
 */
export abstract class APushChannelListener implements IPushChannelListener {
  private _connected = false;
  private _connectListeners: TConnectCallback[] = [];
  private _keepAliveIntervalId: ReturnType<typeof setInterval> | undefined = undefined;
  private _listeners = new Map<string, Set<TPushEventListener>>();
  private _name: string;
  private _wasClosed = false;

  constructor(name?: string) {
    this._name = name ?? "<GenericPushChannelListener>";
    this.on("ajaxCall", async (payload) => this._handleAjaxCall(payload));
    this.on("facesMessage", (payload) => this._handleFacesMessage(payload));
    this.on("remoteCommand", (payload) => this._handleRemoteCommand(payload));
    this.on("executeScript", (payload) => this._handleExecuteScript(payload));
    this.on("crossViewScopeRequest", async (payload) => this._handleCrossViewScopeRequest(payload));
    this.on("redirect", (data) => this._handleRedirect(data));
    this.on("ping", async (payload) => this._handlePing(payload));
  }

  async waitUntilConnected(): Promise<void> {
    if (this._connected) {
      return Promise.resolve(undefined);
    }
    if (this._wasClosed) {
      return Promise.reject(new Error("Web socket was already closed"));
    }
    return new Promise((resolve, reject) => {
      this._connectListeners.push({ reject: reject, resolve: resolve });
    });
  }

  off(eventName: string, handler: TPushEventListener): void {
    this._listeners.get(eventName)?.delete(handler);
  }

  on(eventName: string, handler: TPushEventListener): void {
    let handlers = this._listeners.get(eventName);
    if (handlers === undefined) {
      handlers = new Set();
      this._listeners.set(eventName, handlers);
    }
    handlers.add(handler);
  }

  onClose(message: string, _closeReason: number, _channelName: string, event: CloseEvent): void {
    if (this._keepAliveIntervalId !== undefined) {
      clearInterval(this._keepAliveIntervalId);
    }
    this._connected = false;
    this._wasClosed = true;
    const listeners = this._connectListeners;
    this._connectListeners = [];
    this._keepAliveIntervalId = undefined;
    const error = new Error(`WebSocket was closed with code ${event.code}: ${event.reason}`);
    showNotification(
      {
        detail: message,
        severity: "error",
        summary: `WebSocket error ${event.code} ${event.reason}`,
      },
      undefined,
      false,
    );
    for (const listener of listeners) {
      listener.reject(error);
    }
  }

  onOpen(_channel: string): void {
    this._connected = true;
    this._wasClosed = false;
    const listeners = this._connectListeners;
    this._connectListeners = [];
    const keepAliveInterval = parseDefaultNumber(byId("omniFacesPushKeepAliveInterval")?.textContent, 60000);
    if (keepAliveInterval > 0) {
      this._keepAliveIntervalId = setInterval(() => this._sendKeepAlive(), keepAliveInterval + 1000);
    }
    for (const listener of listeners) {
      listener.resolve();
    }
  }

  onMessage(message: unknown, channel: string): void {
    const json = parseRecord(message);
    if (json === undefined) {
      return;
    }
    const eventName = parseString(json.eventName);
    const payload = parseRecord(json.payload);
    if (eventName === undefined || payload === undefined) {
      return;
    }
    for (const listener of this._listeners.get(eventName) ?? []) {
      void listener(payload, eventName, channel);
    }
  }

  toString(): string {
    return this._name;
  }

  private _sendKeepAlive(): void {
    handleRejection(runRemoteCommand(BackendCommand?.omniFacesPushKeepAlive));
  }

  private async _handleAjaxCall(payload: unknown): Promise<void> {
    const data = parseRecord(payload);

    const actionListener = parseString(data.actionListener);
    const async = parseDefault(data.async, parseBoolean, false);
    const delay = parseDefaultNumber(data.delay, 0);
    const formId = parseDefaultString(data.formId, "clientPushChannelForm");
    const global = parseDefault(data.global, parseBoolean, true);
    const ignoreAutoUpdate = parseDefault(data.ignoreAutoUpdate, parseBoolean, false);
    const oncomplete = parseFunction(data.oncomplete, globalEval);
    const onerror = parseFunction(data.onerror, globalEval);
    const onstart = parseFunction(data.onstart, globalEval);
    const onsuccess = parseFunction(data.onsuccess, globalEval);
    const params = parseServerCallbackParams(data.params);
    const partialSubmit = parseDefault(data.partialSubmit, parseBoolean, false);
    const process = parseString(data.process);
    const resetValues = parseDefault(data.resetValues, parseBoolean, false);
    const source = parseDefaultString(data.source, "clientPushChannelForm:defaultSource");
    const update = parseString(data.update);

    if (notEmpty(actionListener)) {
      params.push({
        name: "de.xima.fc.gui.push.actionListener",
        value: actionListener,
      });
    }

    await PrimeFaces.ajax.Request.handle({
      async,
      delay,
      formId,
      global,
      ignoreAutoUpdate,
      oncomplete: function (xhrOrErrorThrown, status, pfArgs, dataOrXhr): void {
        oncomplete?.call(this, xhrOrErrorThrown, status, pfArgs, dataOrXhr);
      },
      onerror: function (xhr, status, errorThrown): void {
        onerror?.call(this, xhr, status, errorThrown);
      },
      onstart: function (cfg) {
        const result = onstart?.call(this, cfg);
        return parseDefault(result, parseBoolean, true);
      },
      onsuccess: function (data, status, xhr) {
        const result = onsuccess?.call(this, data, status, xhr);
        return notNullish(resetValues) ? parseDefault(result, parseBoolean, false) : false;
      },
      params,
      partialSubmit,
      process,
      resetValues,
      source,
      update,
    });
  }

  private async _handleCrossViewScopeRequest(payload: unknown): Promise<void> {
    const data = parseRecord(payload);
    const requestToken = parseString(data.requestToken);
    const clientRequestData = parseArrayAs(data.clientRequestData, parseRecord);
    const responseDataList: { clientData: Record<string, unknown>; handlerToken: string }[] = [];
    for (const requestData of clientRequestData) {
      const handlerToken = parseString(requestData.handlerToken);
      const clientDataProvider = parseFunction(requestData.clientDataProvider, globalEval);
      let clientDataProviderResult: unknown;
      let error: string | undefined;
      try {
        clientDataProviderResult = await clientDataProvider?.call(window);
        error = undefined;
      } catch (e) {
        console.error("Could not invoke client data callback", e);
        clientDataProviderResult = undefined;
        error = `Could not invoke client data callback: ${String(e)}`;
      }
      const clientData = parseRecord(clientDataProviderResult);
      const responseData = { clientData, error, handlerToken };
      responseDataList.push(responseData);
    }
    await runRemoteCommand(BackendCommand?.Designer?.handleConversationRequest, [
      { name: "requestToken", value: requestToken },
      { name: "responseData", value: JSON.stringify(responseDataList) },
    ]);
  }

  private _handleExecuteScript(payload: unknown): void {
    const data = parseRecord(payload);
    const script = parseString(data.script);
    const nonce = parseString(data.nonce);
    if (notEmpty(script)) {
      if (notEmpty(nonce)) {
        PrimeFaces.csp.eval(script, nonce);
      } else {
        PrimeFaces.csp.eval(script);
      }
    }
  }

  private _handleFacesMessage(payload: unknown): void {
    const messages = parseArrayAs(payload, parseRecord);
    for (const message of messages) {
      const summary = parseString(message.summary);
      const detail = parseString(message.detail);
      const severityString = parseString(message.severity);
      const severity = stringToSeverity(severityString);
      const saveInSession = parseDefault(message.saveInSession, parseBoolean, false);
      showNotification({ detail, severity, summary }, undefined, saveInSession);
    }
  }

  private _handleRedirect(data: unknown): void {
    const payload = parseRecord(data);
    const location = payload.location;
    if (isString(location)) {
      redirect(location);
    } else {
      console.warn("No location for redirection given!");
    }
  }

  private _handlePing(_data: unknown): void {
    // nothing to do, ping successful
  }

  private _handleRemoteCommand(payload: unknown): void {
    const data = parseRecord(payload);
    const path = parseArrayAs(data.remoteCommandName, parseString);
    const params = parseServerCallbackParams(data.remoteCommandParams);
    const remoteCommand = atKeys(window, path);
    if (typeof remoteCommand === "function") {
      remoteCommand(params);
    } else {
      console.warn("Cannot run remote command, no such command found:", path.join("."));
    }
  }
}

export class ViewPushChannelListener extends APushChannelListener {
  constructor(name?: string) {
    super(name ?? "<GenericViewPushChannelListener>");
  }

  static getInstance(): ViewPushChannelListener | undefined {
    return ViewInstance;
  }

  static getOnClose(message: string): TPushChannelCloseListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onClose.bind(instance, message) : undefined;
  }

  static getOnMessage(): TPushChannelListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onMessage.bind(instance) : undefined;
  }

  static getOnOpen(): TPushChannelOpenListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onOpen.bind(instance) : undefined;
  }

  static setGenericInstance(): void {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    this.setInstance(new ViewPushChannelListener());
  }

  static setInstance(listener: ViewPushChannelListener): void {
    ViewInstance = listener;
  }
}

export class SessionPushChannelListener extends APushChannelListener {
  constructor(name?: string) {
    super(name ?? "<GenericSessionPushChannelListener>");
  }

  static getInstance(): SessionPushChannelListener | undefined {
    return SessionInstance;
  }

  static getOnClose(message: string): TPushChannelCloseListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onClose.bind(instance, message) : undefined;
  }

  static getOnMessage(): TPushChannelListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onMessage.bind(instance) : undefined;
  }

  static getOnOpen(): TPushChannelOpenListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onOpen.bind(instance) : undefined;
  }

  static setGenericInstance(): void {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    this.setInstance(new SessionPushChannelListener());
  }

  static setInstance(listener: SessionPushChannelListener): void {
    SessionInstance = listener;
  }
}

export class ApplicationPushChannelListener extends APushChannelListener {
  constructor(name?: string) {
    super(name ?? "<GenericApplicationPushChannelListener>");
  }

  static getInstance(): ApplicationPushChannelListener | undefined {
    return ApplicationInstance;
  }

  static getOnClose(message: string): TPushChannelCloseListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onClose.bind(instance, message) : undefined;
  }

  static getOnMessage(): TPushChannelListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onMessage.bind(instance) : undefined;
  }

  static getOnOpen(): TPushChannelOpenListener | undefined {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    const instance = this.getInstance();
    return instance !== undefined ? instance.onOpen.bind(instance) : undefined;
  }

  static setGenericInstance(): void {
    // biome-ignore lint/complexity/noThisInStatic: Weird design, should be changed. But must be API compatible.
    this.setInstance(new ApplicationPushChannelListener());
  }

  static setInstance(listener: ApplicationPushChannelListener): void {
    ApplicationInstance = listener;
  }
}
